import { css } from "@/domains/emotion";
import { MDSButtonSlotName, MDSButtonVariant } from "@/mds/components/buttons/button/button.types";
import { MDSText } from "@/mds/components/text/text";
import styled from "@emotion/styled";
import { MDSButton, MDSButtonIcon, MDSTextOverflow, MDSTextVariant, MDSTextWeight } from "@/mds";
import { MemCommonEditorActionKind } from "@mem-labs/common-editor";
import { MdsIconKind } from "@/design-system/components/icon";
import { MdsDropdownItem, MdsDropdownItemKind } from "@/design-system/components/dropdown";
import { UNTITLED_TEMPLATE_TITLE } from "@/domains/untitled/untitled";
import { uuidModule } from "@/modules/uuid";
import { MemCommonEditorStore } from "@/components/note/store/MemCommonEditorStore";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store/utils/hooks";
import { TrackedEvent, TrackedEventContext, trackEvent } from "@/domains/metrics";
import { RecordingStatus } from "@/store/audio-capture/AppStoreAudioCaptureStore";
import { HoverActions } from "@/components/note/editor/HoverActions";
import { NoteType } from "@/components/note/types";

export const EmptyNoteActions = observer(
  ({ editorStore }: { editorStore: MemCommonEditorStore }) => {
    const { store } = useAppStore();

    const noteId = editorStore.id;
    const canStartRecording =
      store.audioCapture.status === RecordingStatus.IDLE && editorStore.noteType === NoteType.Note;

    return (
      <StartWithContainer>
        <StartWithLabel
          variant={MDSTextVariant.TextXs}
          overflow={MDSTextOverflow.Ellipsis}
          weight={MDSTextWeight.SemiBold}
        >
          Start with
        </StartWithLabel>
        <StartWithOptionsContainer>
          {canStartRecording && (
            <MDSButton
              variant={MDSButtonVariant.Subtle}
              onClick={() => {
                if (noteId) store.audioCapture.startRecording({ noteId });
              }}
            >
              <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.Microphone} />
              Voice
            </MDSButton>
          )}
          {editorStore.noteType === NoteType.Note && !store.templates.ids.size && (
            <MDSButton
              variant={MDSButtonVariant.Subtle}
              onClick={async () => {
                const templateId = uuidModule.generate();
                await store.templates.createTemplate({ templateId });
                store.navigation.goToTemplate({ templateId });
              }}
            >
              <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.Plus} />
              Create template
            </MDSButton>
          )}
          {editorStore.noteType === NoteType.Note && store.templates.ids.size > 0 && (
            <HoverActions
              label="Templates"
              iconKind={MdsIconKind.AlignLeft}
              contentList={{
                items: [
                  ...store.templates.sortedRecentTemplatesInteractedWithByMe.map(
                    ({ template }): MdsDropdownItem => ({
                      id: template.id,
                      kind: MdsDropdownItemKind.Button,
                      label: template.title || UNTITLED_TEMPLATE_TITLE,
                      iconKind: MdsIconKind.Template,
                      onClick: async () => {
                        const templateContents =
                          await template.getTemplateContentsForInsertionAsync();
                        if (!templateContents) return;

                        const { remoteContent, documentUpdates } = templateContents;

                        editorStore.dispatchAction({
                          kind: MemCommonEditorActionKind.InsertTemplate,
                          payload: {
                            templateId: template.id,
                            templateRemoteContent: remoteContent,
                            templateDocumentUpdates: documentUpdates,
                            templateVariables: Object.entries(store.templates.getVariables()).map(
                              ([code, variable]) => ({
                                code,
                                value: variable.value,
                              })
                            ),
                          },
                        });
                        trackEvent(TrackedEvent.TemplateUsed, {
                          template_name: template.title || UNTITLED_TEMPLATE_TITLE,
                          context: TrackedEventContext.FloatingBar,
                        });
                      },
                    })
                  ),
                  {
                    id: "divider",
                    kind: MdsDropdownItemKind.Divider,
                    className:
                      store.templates.sortedRecentTemplatesInteractedWithByMe.length <= 2
                        ? displayNone
                        : undefined,
                  },
                  {
                    id: "manage-templates",
                    kind: MdsDropdownItemKind.Button,
                    label: "Manage templates",
                    iconKind: MdsIconKind.GearCode,
                    onClick: () => {
                      store.navigation.goToTemplates();
                    },
                  },
                ],
              }}
            />
          )}
          <MDSButton
            variant={MDSButtonVariant.Subtle}
            onClick={() =>
              editorStore.dispatchAction({
                kind: MemCommonEditorActionKind.ToggleChecklistTextFormat,
                payload: null,
              })
            }
          >
            <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.ListCheck} />
            Checklist
          </MDSButton>
          <MDSButton
            variant={MDSButtonVariant.Subtle}
            onClick={() =>
              editorStore.dispatchAction({
                kind: MemCommonEditorActionKind.InsertTable,
                payload: { columns: 2, rows: 2 },
              })
            }
          >
            <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.Table} />
            Table
          </MDSButton>
        </StartWithOptionsContainer>
      </StartWithContainer>
    );
  }
);

const displayNone = css({
  display: "none",
});

const StartWithContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: ${({ theme }) =>
    `${theme.spacing["3"]} ${theme.spacing["3"]} ${theme.spacing["3"]} ${theme.spacing["7"]}`};
  gap: ${({ theme }) => theme.spacing["4"]};
`;

const StartWithLabel = styled(MDSText)`
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const StartWithOptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.xs};
`;
